// Cobranzas.actions.ts
// Consulta del payload de campañas de cobranza (ms-proveedores via gateway):
// GET {VITE_API_URL}/api/v1/proveedores/campanias/{preventiva|recordatorio|corte}?solo_payload=true
// Devuelve el payload que se enviaría a Genesys (no inicia la campaña):
// contactos de Softnet + deuda Laravel. Puede tardar un minuto o más.
//
// Envío de lista de contactos a Genesys via api-gateway + eureka (ms-proveedores).
// El envío es ASINCRONO: iniciar devuelve al instante un id de ejecución, y el
// frontend hace poll a /estado/{id} hasta que termina (CORRIENDO -> COMPLETADO |
// INTERRUMPIDO | ERROR). Se puede cancelar con /cancelar/{id}. Requiere token de
// sesion (auth=true) en todas las llamadas.
//
// Iniciar:     POST {VITE_API_URL}/api/v1/proveedores/campanias/iniciar/{preventiva|recordatorio|corte}
// Estado:      GET  {VITE_API_URL}/api/v1/proveedores/campanias/estado/{id}
// Cancelar:    POST {VITE_API_URL}/api/v1/proveedores/campanias/cancelar/{id}
// Errores:     GET  {VITE_API_URL}/api/v1/proveedores/campanias/errores?campania={tipo}

import { useMutation } from "@tanstack/react-query";
import { erpApi } from "@/shared/axios";
import { urlBackend } from "@/shared/constants";
import type {
  CobranzasCampaignResponse,
  CobranzasCampaignType,
  CobranzasGenesysCancelarResponse,
  CobranzasGenesysEstadoResponse,
  CobranzasGenesysIniciarResponse,
} from "@/erp/campanas/cobranzas";

const { get, post } = erpApi();

const campaignEndpoint: Record<CobranzasCampaignType, string> = {
  PREVENTIVA: "preventiva",
  RECORDATORIO: "recordatorio",
  CORTE: "corte",
};

export const getCobranzasCampaign = async (
  type: CobranzasCampaignType,
  soloConValor?: boolean,
): Promise<CobranzasCampaignResponse> => {
  // solo_payload=true: devuelve el JSON del payload sin iniciar la campaña.
  // solo_con_valor: solo registros con valor mayor que 0.
  // La consulta puede tardar un minuto o más; erpApi usa timeout de 10 min.
  const params = new URLSearchParams({ solo_payload: "true" });
  if (soloConValor) {
    params.set("solo_con_valor", "true");
  }

  const response = await get<CobranzasCampaignResponse>(
    `${urlBackend}api/v1/proveedores/campanias/${campaignEndpoint[type]}?${params.toString()}`,
    true,
  );

  return response as unknown as CobranzasCampaignResponse;
};

///* tanStack mutation (mismo estilo que las acciones Softnet del repo)
export const useCobranzasCampaignMutation = () => {
  return useMutation({
    mutationFn: ({
      type,
      soloConValor,
    }: {
      type: CobranzasCampaignType;
      soloConValor?: boolean;
    }) => getCobranzasCampaign(type, soloConValor),
  });
};

export const iniciarCobranzasGenesys = async (
  type: CobranzasCampaignType,
): Promise<CobranzasGenesysIniciarResponse> => {
  // POST: devuelve al instante 202 {status, id, campania, estado: "CORRIENDO"}.
  // 409 si ya hay un envío en curso de la misma campaña (también trae el id activo).
  const response = await post<CobranzasGenesysIniciarResponse>(
    `${urlBackend}api/v1/proveedores/campanias/iniciar/${campaignEndpoint[type]}`,
    {},
    true,
  );

  return response as unknown as CobranzasGenesysIniciarResponse;
};

///* tanStack mutation para iniciar el envio asincrono a Genesys
export const useIniciarCobranzasGenesysMutation = () => {
  return useMutation({
    mutationFn: (type: CobranzasCampaignType) => iniciarCobranzasGenesys(type),
  });
};

export const getCobranzasGenesysEstado = async (
  id: string,
): Promise<CobranzasGenesysEstadoResponse> => {
  // 404 si la ejecución ya no existe en el micro (ej: reinicio del micro).
  const response = await get<CobranzasGenesysEstadoResponse>(
    `${urlBackend}api/v1/proveedores/campanias/estado/${id}`,
    true,
  );

  return response as unknown as CobranzasGenesysEstadoResponse;
};

export const getCobranzasGenesysErrores = async (
  type: CobranzasCampaignType,
): Promise<unknown> => {
  // Contactos que fallaron en la campaña, persistidos en BD.
  const response = await get<unknown>(
    `${urlBackend}api/v1/proveedores/campanias/errores?campania=${campaignEndpoint[type]}`,
    true,
  );

  return response;
};

export const cancelarCobranzasGenesys = async (
  id: string,
): Promise<CobranzasGenesysCancelarResponse> => {
  // Pide la cancelacion; el micro corta entre bloques y pasa el estado a INTERRUMPIDO.
  const response = await post<CobranzasGenesysCancelarResponse>(
    `${urlBackend}api/v1/proveedores/campanias/cancelar/${id}`,
    {},
    true,
  );

  return response as unknown as CobranzasGenesysCancelarResponse;
};

///* tanStack mutation para cancelar el envio asincrono a Genesys
export const useCancelarCobranzasGenesysMutation = () => {
  return useMutation({
    mutationFn: (id: string) => cancelarCobranzasGenesys(id),
  });
};
